import {AGENCY_KEYS} from "@/api/agencyApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import UserAvatarPlaceholder from "@/assets/images/user_avatar_placeholder.svg";
import Modal from "@/components/Common/Modal";
import {useLogout} from "@/hooks/useLogout";
import {useUserId} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import type {TAccountMember} from "@/store/types";
import {useMutation} from "@tanstack/react-query";
import type {FC} from "react";
import {useTranslation} from "react-i18next";

const RemoveCompanyMemberModal: FC<{
  companyId: number;
  companyName: string;
  companyMember: TAccountMember;
  isAgency?: boolean;
  onClose: () => void;
}> = ({companyId, companyName, companyMember, isAgency = false, onClose}) => {
  const {t} = useTranslation("multi_account");
  const logout = useLogout();
  const loggedInUserId = useUserId();
  const isRemovingSelf = companyMember.user.id === loggedInUserId;

  const removalKey = isAgency
    ? AGENCY_KEYS.mAgencyMemberRemoval(companyId)
    : COMPANY_KEYS.mCompanyMemberRemoval(companyId);
  const membersQueryKey = isAgency ? AGENCY_KEYS.qAgencyMembers(companyId) : COMPANY_KEYS.qCompanyMembers(companyId);

  const {mutate: removeMember} = useMutation({
    mutationKey: removalKey,
    mutationFn: () => client.post(removalKey[0], {user_id: companyMember.user.id}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: membersQueryKey});

      if (isRemovingSelf) {
        logout();
      }
    },
  });

  const leaveTitleKey = isAgency ? "leave_agency" : "leave_company";
  const leaveConfirmationKey = isAgency ? "leave_agency_confirmation" : "leave_company_confirmation";
  const leaveWarningKey = isAgency ? "leave_agency_warning" : "leave_company_warning";
  const fromKey = isAgency ? "from_agency" : "from_company";

  return (
    <Modal
      backgroundClassName="max-w-150"
      modalTitle={t(isRemovingSelf ? leaveTitleKey : "remove_user_from_account")}
      onClose={onClose}
      onConfirm={removeMember}
      confirmButtonStyle="Danger"
      confirmButtonText={t(isRemovingSelf ? leaveTitleKey : "remove_user")}>
      <div className="flex flex-col items-center gap-5">
        {!isRemovingSelf && <span className="text-neutral-black">{t("remove_user")}</span>}
        <div className="border-neutral-grey-light-dark flex items-center gap-2 rounded-full border p-1 pr-2">
          <div className="size-6 shrink-0 rounded-full">
            <img
              src={companyMember?.user.profile_picture ?? UserAvatarPlaceholder}
              className="h-full w-full rounded-full"
            />
          </div>
          <span className="bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent">
            {companyMember?.user.first_name} {companyMember?.user.last_name}
          </span>
        </div>
        <span className="text-neutral-black">
          {t(isRemovingSelf ? leaveConfirmationKey : fromKey, {
            companyName: companyName,
          })}
        </span>
        <div className="bg-warning-light-yellow text-warning-dark-yellow self-stretch rounded-md px-4 py-5 text-center text-lg font-semibold">
          {t(isRemovingSelf ? leaveWarningKey : "remove_user_warning")}
        </div>
      </div>
    </Modal>
  );
};

export default RemoveCompanyMemberModal;
